import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { type AccessRoleCode, AccessRoleCodes } from "Models/AccessRoleCodes";
import type { CodeListItem } from "Models/ICodeListDto";
import {
  Resources,
  type ResourceDictionary,
  useResource,
} from "Translations/Resources";
import type { ObjPathProxy } from "ts-object-path";

/**
 * Category codes. The authoritative definition lives on the backend
 * (`Domain/Support/Models/SupportRequestCategory.cs`) — the API rejects an unknown value with a
 * 400, so the two lists drifting apart shows up immediately. This enum cannot be generated into
 * `Api.ts`: for multipart/form-data NSwag flattens every field to a primitive, so the enum never
 * reaches Swagger at all.
 *
 * Customer-facing labels are in the dictionaries below; the Czech labels for support live in Brain
 * (`server/src/modules/intake/domain/categories.ts`).
 */
export enum SupportRequestCategory {
  ClientOnboarding = "ClientOnboarding",
  CommissionsAndPayouts = "CommissionsAndPayouts",
  ValorPoints = "ValorPoints",
  TechnicalIssues = "TechnicalIssues",
  StardustPoints = "StardustPoints",
  PaymentIssues = "PaymentIssues",
  AccountAndSignIn = "AccountAndSignIn",
  TransactionComplaints = "TransactionComplaints",
  HardwareAndConnectivity = "HardwareAndConnectivity",
  FinanceAndSettlement = "FinanceAndSettlement",
  AccountAdministration = "AccountAdministration",
  Other = "Other",
}

/** Which support queue the report goes to. The server derives this from the token for real; this
    copy only decides what the form offers. */
export type SupportRequestRole = "user" | "merchant" | "hunter";

/**
 * Mirrored from `Domain/Support/Models/SupportRequestCategories.cs`, which is authoritative and
 * enforces it — Brain's own map is display-only and would happily file a Hunter category on a
 * Customer report. Offering a category the server rejects is a 400, so the two must agree.
 */
export const SUPPORT_ROLE_CATEGORIES: Record<
  SupportRequestRole,
  ReadonlyArray<SupportRequestCategory>
> = {
  user: [
    SupportRequestCategory.StardustPoints,
    SupportRequestCategory.PaymentIssues,
    SupportRequestCategory.AccountAndSignIn,
    SupportRequestCategory.Other,
  ],
  merchant: [
    SupportRequestCategory.TransactionComplaints,
    SupportRequestCategory.HardwareAndConnectivity,
    SupportRequestCategory.FinanceAndSettlement,
    SupportRequestCategory.AccountAdministration,
    SupportRequestCategory.Other,
  ],
  hunter: [
    SupportRequestCategory.ClientOnboarding,
    SupportRequestCategory.CommissionsAndPayouts,
    SupportRequestCategory.ValorPoints,
    SupportRequestCategory.TechnicalIssues,
    SupportRequestCategory.Other,
  ],
};

// Exhaustive on purpose: a role added to AccessRoleCodes should fail the build here rather than
// quietly fall into the customer queue.
const ACCESS_ROLE_TO_SUPPORT_ROLE: Record<AccessRoleCode, SupportRequestRole> =
  {
    [AccessRoleCodes.Merchant]: "merchant",
    [AccessRoleCodes.Hunter]: "hunter",
    [AccessRoleCodes.HunterPlus]: "hunter",
    [AccessRoleCodes.Admin]: "user",
    [AccessRoleCodes.AdminLite]: "user",
    [AccessRoleCodes.Aml]: "user",
    [AccessRoleCodes.Customer]: "user",
    [AccessRoleCodes.CustomerOnboarding]: "user",
    [AccessRoleCodes.Employee]: "user",
    [AccessRoleCodes.Disponent]: "user",
    [AccessRoleCodes.Child]: "user",
  };

/**
 * The queue the current caller reports into. `useCurrentUserRole` is the active context's role —
 * switching contexts hard-reloads the app, so it can't go stale — and is null for an anonymous
 * visitor and during bootstrap, both of which belong in the customer queue.
 */
export const useSupportRequestRole = (): SupportRequestRole => {
  const userRole = useCurrentUserRole();

  return userRole ? ACCESS_ROLE_TO_SUPPORT_ROLE[userRole] : "user";
};

const RESOURCES = Resources.Support.Request.Categories;

const CATEGORY_RESOURCES: Record<
  SupportRequestCategory,
  ObjPathProxy<ResourceDictionary, string>
> = {
  [SupportRequestCategory.ClientOnboarding]: RESOURCES.ClientOnboarding,
  [SupportRequestCategory.CommissionsAndPayouts]:
    RESOURCES.CommissionsAndPayouts,
  [SupportRequestCategory.ValorPoints]: RESOURCES.ValorPoints,
  [SupportRequestCategory.TechnicalIssues]: RESOURCES.TechnicalIssues,
  [SupportRequestCategory.StardustPoints]: RESOURCES.StardustPoints,
  [SupportRequestCategory.PaymentIssues]: RESOURCES.PaymentIssues,
  [SupportRequestCategory.AccountAndSignIn]: RESOURCES.AccountAndSignIn,
  [SupportRequestCategory.TransactionComplaints]:
    RESOURCES.TransactionComplaints,
  [SupportRequestCategory.HardwareAndConnectivity]:
    RESOURCES.HardwareAndConnectivity,
  [SupportRequestCategory.FinanceAndSettlement]: RESOURCES.FinanceAndSettlement,
  [SupportRequestCategory.AccountAdministration]:
    RESOURCES.AccountAdministration,
  [SupportRequestCategory.Other]: RESOURCES.Other,
};

export const useSupportRequestCategoryCodeList = (): Array<CodeListItem> => {
  const { t } = useResource();
  const role = useSupportRequestRole();

  return SUPPORT_ROLE_CATEGORIES[role].map((category) => ({
    code: category,
    name: t(CATEGORY_RESOURCES[category]),
  }));
};
